import {
  type ElementType,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  type RefObject,
  useCallback,
  useId,
  useState,
} from "react";

import {
  Box,
  CheckIcon,
  Column,
  Row,
  SearchInput,
  Skeleton,
  SkeletonBox,
  Text,
  TextWithTooltip,
  useMergeRefs,
} from "@hightouch/htui";

import { scrollOptionIntoView } from "src/formkit/components/mapper-v2/hooks/use-mapper-listbox";
import { useScrollFade } from "src/hooks/use-scroll-fade";

const SKELETON_LABEL_WIDTHS = ["40%", "55%", "30%", "45%", "60%", "35%"];

/** A pane's main control, or a handle that moves focus to it. */
export type MapperFocusTarget = { focus: () => void };

type MapperSearchProps = {
  /** Panes that focus their search box first attach their focus target here. */
  inputRef?: Ref<HTMLInputElement>;
  /** The search box's accessible name. */
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  /** Wires the search box to the listbox it filters, as a combobox. */
  listbox?: {
    listId: string;
    hasOptions: boolean;
    activeOptionId: string | undefined;
    handleKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  };
  /** Controls after the search box, such as a refresh button. */
  actions?: ReactNode;
  /** Space around the search box, for panes that lay it out in their own row. */
  padding?: 0 | 2;
};

/** The search box at the top of a pane. */
export function MapperSearch({
  inputRef,
  label,
  placeholder,
  value,
  onChange,
  listbox,
  actions,
  padding = 2,
}: MapperSearchProps) {
  return (
    <Row alignItems="center" flexShrink={0} gap={2} p={padding}>
      <Box flex={1} minWidth={0}>
        <SearchInput
          ref={inputRef}
          aria-activedescendant={listbox?.activeOptionId}
          aria-autocomplete={listbox ? "list" : undefined}
          aria-controls={listbox?.hasOptions ? listbox.listId : undefined}
          aria-expanded={listbox ? listbox.hasOptions : undefined}
          aria-label={label}
          placeholder={placeholder}
          role={listbox ? "combobox" : undefined}
          value={value}
          width="100%"
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={listbox?.handleKeyDown}
        />
      </Box>
      {actions}
    </Row>
  );
}

type MapperListProps = {
  listId: string;
  scrollRef: RefObject<HTMLDivElement | null>;
  /** The listbox's active option, which Enter chooses. */
  activeOptionId: string | undefined;
  /** The listbox's accessible name. */
  label: string;
  /** Content above the options inside the scroll area, such as an alert. */
  header?: ReactNode;
  children: ReactNode;
};

/**
 * A pane's scrolling list of options. It opens scrolled to the active option,
 * or, when no option is active, with the chosen option centered if it starts
 * out of view.
 */
export function MapperList({
  listId,
  scrollRef,
  activeOptionId,
  label,
  header,
  children,
}: MapperListProps) {
  const scrollFade = useScrollFade();
  // The listbox scrolls the active option into view as it moves, so only the one at mount matters here.
  const [initialActiveOptionId] = useState(activeOptionId);
  const scrollToInitialOption = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element) return;
      const active = initialActiveOptionId
        ? element.ownerDocument.getElementById(initialActiveOptionId)
        : null;
      const selected = element.querySelector<HTMLElement>(
        '[role="option"][aria-selected="true"]',
      );
      if (active) {
        scrollOptionIntoView(element, active, "nearest");
      } else if (
        selected &&
        selected.offsetTop + selected.offsetHeight >
          element.scrollTop + element.clientHeight
      ) {
        scrollOptionIntoView(element, selected, "center");
      }
      // Stopping partway through the header would hide its top, such as an alert's title and dismiss button.
      const listbox = element.querySelector<HTMLElement>('[role="listbox"]');
      if (
        listbox &&
        element.scrollTop > 0 &&
        element.scrollTop < listbox.offsetTop
      ) {
        element.scrollTop = listbox.offsetTop;
      }
    },
    [initialActiveOptionId],
  );
  const ref = useMergeRefs(scrollRef, scrollFade.ref, scrollToInitialOption);

  return (
    <Column
      ref={ref}
      flex={1}
      minHeight={0}
      overflowY="auto"
      position="relative"
      px={2}
      py={2}
      sx={scrollFade.sx}
    >
      {header}
      <Column aria-label={label} id={listId} role="listbox">
        {children}
      </Column>
    </Column>
  );
}

type MapperListSectionProps = {
  label: string;
  children: ReactNode;
};

/** A labeled group of options inside a list. */
export function MapperListSection({ label, children }: MapperListSectionProps) {
  const headingId = useId();
  return (
    <Column aria-labelledby={headingId} role="group">
      <Row alignItems="center" id={headingId} minHeight={8} px={2} py={1}>
        <Text color="text.secondary" fontWeight="medium" size="sm">
          {label}
        </Text>
      </Row>
      {children}
    </Column>
  );
}

/** A 16px icon centered in the 20px slot rows lead with. */
export function MapperRowIcon({ icon }: { icon: ElementType }) {
  return (
    <Row alignItems="center" boxSize={5} justifyContent="center">
      <Box as={icon} boxSize={4} color="icon.tertiary" />
    </Row>
  );
}

type MapperListRowProps = {
  id: string;
  label: string;
  /** A 20px icon before the label. */
  icon?: ReactNode;
  /** Badges or markers after the label. */
  accessory?: ReactNode;
  description?: ReactNode;
  /** Shows the description on its own line instead of after the label. */
  hasDescriptionBelow?: boolean;
  /** The option's source, in a cell on the right. */
  source?: ReactNode;
  isActive: boolean;
  isSelected: boolean;
  onChoose: () => void;
};

/**
 * One option in a pane's list. The keyboard-active option shares the hover
 * background; the chosen option is tinted and checked.
 */
export function MapperListRow({
  id,
  label,
  icon,
  accessory,
  description,
  hasDescriptionBelow = false,
  source,
  isActive,
  isSelected,
  onChoose,
}: MapperListRowProps) {
  const activeBg = isSelected ? "primary.backgroundHover" : "base.background";
  return (
    <Row
      _hover={{ bg: activeBg }}
      alignItems="center"
      aria-selected={isSelected}
      bg={isActive ? activeBg : isSelected ? "primary.background" : undefined}
      borderRadius="lg"
      cursor="pointer"
      flexShrink={0}
      gap={4}
      id={id}
      minHeight={10}
      p={2}
      role="option"
      onClick={onChoose}
      // Keeps focus in the search box, which drives the list.
      onMouseDown={(event) => event.preventDefault()}
    >
      <Row
        alignItems={hasDescriptionBelow ? "flex-start" : "center"}
        flex={1}
        gap={2}
        minWidth={0}
      >
        {icon && <Row flexShrink={0}>{icon}</Row>}
        <Column gap={0.5} minWidth={0}>
          <Row alignItems="center" gap={2} minWidth={0}>
            <TextWithTooltip color="text.secondary" fontWeight="medium">
              {label}
            </TextWithTooltip>
            {accessory}
            {description && !hasDescriptionBelow && (
              <TextWithTooltip color="text.tertiary" size="sm">
                {description}
              </TextWithTooltip>
            )}
          </Row>
          {description && hasDescriptionBelow && (
            <Text color="text.tertiary" size="sm">
              {description}
            </Text>
          )}
        </Column>
      </Row>
      {source && (
        <Row alignItems="center" flex={1} gap={2} minWidth={0}>
          {source}
        </Row>
      )}
      {/* Rows with a source cell keep the check's slot, so their source cells line up whether or not a row is chosen. */}
      {(isSelected || source) && (
        <Box boxSize={5} flexShrink={0}>
          {isSelected && (
            <Box as={CheckIcon} boxSize={5} color="primary.base" />
          )}
        </Box>
      )}
    </Row>
  );
}

/** Loading rows at the height of real options. */
export function MapperListSkeleton() {
  return (
    <Skeleton isLoading>
      <Column px={2} py={2}>
        {SKELETON_LABEL_WIDTHS.map((width) => (
          <Row key={width} alignItems="center" gap={2} minHeight={10} p={2}>
            <SkeletonBox
              borderRadius="md"
              flexShrink={0}
              height={5}
              width={5}
            />
            <SkeletonBox borderRadius="sm" height={4} width={width} />
          </Row>
        ))}
      </Column>
    </Skeleton>
  );
}

/** The bar at the bottom of a pane that commits its value. */
export function MapperPaneFooter({ children }: { children: ReactNode }) {
  return (
    <Row flexShrink={0} justifyContent="flex-end" pb={4} pt={2} px={4}>
      {children}
    </Row>
  );
}
